/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useEffect, useState, useRef } from "react";
import { CircleX, Eye, EyeOff, Info } from "lucide-react";
import ToolTip from "../../../ToolTip/ToolTip";
import { CopyableInput } from "./CopyableInput";
import { DateTimePicker } from "./DateTimePicker";
import { DateTimePickerMode, InputProps } from "../interfaces";

// Constants
const DEFAULT_PLACEHOLDER: string = "Escribe algo";

/**
 * Date-like types are not rendered as native inputs: each browser paints those
 * controls its own way, so they are delegated to the dashboard's own picker,
 * which emits the very same value string.
 */
const DATE_PICKER_MODES: Record<string, DateTimePickerMode> = {
  "datetime-local": "datetime",
  date: "date",
  time: "time",
};

export const Input: React.FC<InputProps> = (props) => {
  const {
    autoFocus,
    className,
    disabled,
    error,
    handleDelete,
    icon: Icon,
    id,
    isCopyable,
    isRequired,
    label,
    leftAdornment,
    limit = 200,
    name,
    onChange,
    onKeyDown,
    placeholder = DEFAULT_PLACEHOLDER,
    reference,
    rightAdornment,
    style,
    tooltipText,
    type = "text",
    value,
    onBlur: externalOnBlur,
    onFocus: externalOnFocus,
    ...remainingProps
  } = props;
  const [inputType, setInputType] = useState<string>(type);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputType = (): void =>
    setInputType(inputType === "password" ? "text" : "password");

  const handleChange = (e: React.FormEvent<HTMLInputElement>): void => {
    if (!e.currentTarget) {
      return;
    }

    if (inputType === "number") {
      let value: string = e.currentTarget.value;

      // Auto-prepend "0" when value starts with "." to allow formats like .8 -> 0.8
      if (value.startsWith(".")) {
        value = "0" + value;
      }

      // Allow integers, decimals, and values ending with a dot (while typing)
      const isNumber: RegExp = /^[0-9]*\.?[0-9]*$/;
      if (isNumber.test(value)) {
        const number: number = Number(value);
        if (number < 0) {
          return onChange?.("0");
        }
        return onChange?.(value);
      } else {
        if (e.currentTarget.value === "") {
          return onChange?.("");
        }
        if (e.currentTarget.value.length > limit) {
          return;
        }
        return;
      }
    }

    if (e.currentTarget.value.length > limit) {
      return;
    }
    onChange?.(e.currentTarget.value || "");
  };

  useEffect(() => {
    setInputType(type);
  }, [type]);

  const datePickerMode: DateTimePickerMode | undefined =
    DATE_PICKER_MODES[inputType];

  if (datePickerMode) {
    return (
      <DateTimePicker
        className={className}
        disabled={disabled}
        error={error}
        isCopyable={isCopyable}
        isRequired={isRequired}
        label={label}
        mode={datePickerMode}
        name={name}
        onChange={onChange}
        // The generic default ("Escribe algo") means nothing on a picker, so it
        // falls through to the per-mode placeholder unless a screen set its own.
        placeholder={
          placeholder === DEFAULT_PLACEHOLDER ? undefined : placeholder
        }
        tooltipText={tooltipText}
        value={value}
      />
    );
  }

  return isCopyable ? (
    <CopyableInput
      isRequired={isRequired}
      label={label}
      tooltipText={tooltipText}
      value={value}
    />
  ) : (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-normal text-gray-500">
            {label}{" "}
            {isRequired && (
              <span className="text-xs font-normal text-gray-500">*</span>
            )}
          </label>
          {tooltipText && (
            <ToolTip text={tooltipText}>
              <Info className="h-3 w-3 shrink-0 text-gray-500" />
            </ToolTip>
          )}
        </div>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            <Icon className="h-4 w-4 shrink-0 stroke-gray-400" />
          </div>
        )}
        {leftAdornment && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            {leftAdornment}
          </div>
        )}
        <input
          id={id}
          ref={reference || inputRef}
          name={name}
          type={inputType}
          autoFocus={autoFocus}
          className={`h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none disabled:bg-gray-300/20 disabled:text-gray-500 disabled:opacity-100 ${
            Icon || leftAdornment ? "pl-9" : ""
          }
        ${handleDelete || rightAdornment || type === "password" ? "pr-9" : ""}
        ${className ? className : ""}
        ${
          inputType === "number"
            ? "appearance-none [&::-moz-inner-spin-button]:appearance-none [&::-moz-outer-spin-button]:appearance-none [&::-ms-inner-spin-button]:appearance-none [&::-ms-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            : ""
        }
        `}
          placeholder={placeholder}
          style={style}
          value={value}
          onChange={handleChange}
          onFocus={externalOnFocus}
          onBlur={externalOnBlur}
          disabled={disabled}
          onWheel={(e) => e.currentTarget.blur()}
          onKeyDown={onKeyDown}
          {...remainingProps}
        />
        {rightAdornment && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightAdornment}
          </div>
        )}
        {handleDelete && (
          <div
            className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
            onClick={handleDelete}
          >
            <CircleX className="h-4 w-4 shrink-0 stroke-gray-400" />
          </div>
        )}
        {type === "password" && (
          <div
            className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
            onClick={handleInputType}
          >
            {inputType === "password" ? (
              <Eye className="h-4 w-4 shrink-0 stroke-primary" />
            ) : (
              <EyeOff className="h-4 w-4 shrink-0 stroke-primary" />
            )}
          </div>
        )}
      </div>
      {error && <label className="text-xs text-red-500">{error}</label>}
    </div>
  );
};
